import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
  ResponsiveContainer
} from 'recharts';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import DemoBlockModal from '../components/DemoBlockModal';
import { isDemoUser } from '../utils/demoGuard';
import './DemographicsDashboard.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';

const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;
const TABLE_NAME = BIGQUERY_CONFIG.TABLE;

// Formatter functions
const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatLOS = (value) => (Number(value || 0)).toFixed(1);
const formatRiskScore = (value) => (Number(value || 0)).toFixed(3);
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

function ChronicConditionsDashboard() {
  const { user, getAuthHeader } = useAuth();
  const [showDemoBlock, setShowDemoBlock] = React.useState(false);
  const [metrics, setMetrics] = useState({
    totalBenes: 0,
    personYears: 0,
    avgAge: 0,
    avgRiskScore: 0,
    patientsWithCC: 0
  });
  const [data, setData] = useState({
    byChronicCondition: [],
    byComorbidityLevel: []
  });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [filters, setFilters] = useState({
    'BENE_MBI_ID': ['All'],
    'BENE_1ST_NAME': ['All'],
    'BENE_LAST_NAME': ['All'],
    'GENDER': ['All'],
    'COUNTY': ['All'],
    'COHORT': ['All'],
    'CHRONIC': ['All'],
    'DEATH': ['All'],
    'STATE': ['All']
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (field, values) => {
    setFilters(prev => ({ ...prev, [field]: values }));
  };

  const handleResetFilters = () => {
    setFilters({
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All'],
      'GENDER': ['All'],
      'COUNTY': ['All'],
      'COHORT': ['All'],
      'CHRONIC': ['All'],
      'DEATH': ['All'],
      'STATE': ['All']
    });
  };

  const buildFilterParams = () => {
    let params = `project=${PROJECT_ID}&dataset=${DATASET_NAME}&table=${TABLE_NAME}`;
    
    Object.entries(filters).forEach(([field, values]) => {
      if (!values.includes('All') && values.length > 0) {
        const filterValue = values.join(',');
        params += `&${field.toLowerCase()}=${encodeURIComponent(filterValue)}`;
      }
    });
    
    return params;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const params = buildFilterParams();
      
      const axiosConfig = { 
        timeout: 180000
      };
      
      const combinedRes = await axios.get(`${API_BASE_URL}/api/chronic/combined?${params}`, axiosConfig);
      
      if (combinedRes.data.success) {
        const responseData = combinedRes.data;
        
        setMetrics({
          totalBenes: responseData.metrics.total_benes || 0,
          personYears: responseData.metrics.person_years || 0,
          avgAge: responseData.metrics.avg_age || 0,
          avgRiskScore: responseData.metrics.avg_risk_score || 0,
          patientsWithCC: responseData.metrics.patients_with_cc || 0
        });
        
        // Calculate percentages
        const totalBenes = responseData.metrics.total_benes || 1;
        
        const byChronicWithPercent = (responseData.by_chronic || []).map(item => ({
          ...item,
          percentage: ((item.membership / totalBenes) * 100).toFixed(1)
        }));
        
        const byComorbidityWithPercent = (responseData.by_comorbidity || []).map(item => ({
          ...item,
          percentage: ((item.membership / totalBenes) * 100).toFixed(1)
        }));
        
        console.log('Comorbidity data:', byComorbidityWithPercent);
        
        setData({
          byChronicCondition: byChronicWithPercent,
          byComorbidityLevel: byComorbidityWithPercent
        });
        
        // Fetch beneficiaries (limit 15)
        try {
          const beneficiariesRes = await axios.get(`${API_BASE_URL}/api/demographics/beneficiaries?${params}&limit=15`, { 
            timeout: 60000
          });
          setBeneficiaries(beneficiariesRes.data.data || []);
        } catch (benefError) {
          console.error('Error loading beneficiaries:', benefError);
          setBeneficiaries([]);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load data. Error: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (isDemoUser(user)) { setShowDemoBlock(true); return; }
    if (beneficiaries.length === 0) return;
    
    const headers = Object.keys(beneficiaries[0]);
    const csvContent = [
      headers.join(','),
      ...beneficiaries.map(row => 
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chronic_conditions_beneficiaries.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="page-with-sidebar">
        <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
        <div className="dashboard">
          <h1 className="dashboard-title">⚕️ Chronic Conditions Dashboard</h1>
          <div className="metrics-row">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="metric-card" style={{
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`,
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}>
                <div style={{height: '14px', width: '80px', backgroundColor: '#d1d5db', borderRadius: '4px', marginBottom: '8px'}}></div>
                <div style={{height: '28px', width: '100px', backgroundColor: '#d1d5db', borderRadius: '4px'}}></div>
              </div>
            ))}
          </div>
          <div className="charts-grid" style={{gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '24px'}}>
            {[1, 2].map((i) => (
              <div key={i} className="chart-card" style={{
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${(i + 5) * 0.1}s`,
                height: '500px',
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}></div>
            ))}
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="page-with-sidebar">
      {showDemoBlock && <DemoBlockModal onClose={() => setShowDemoBlock(false)} />}
      <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
      <div className="dashboard">
        <h1 className="dashboard-title">⚕️ Chronic Conditions Dashboard</h1>
        
        <div style={{animation: 'fadeIn 0.5s ease-in-out'}}>
          <div className="metrics-row">
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>👥</span>
                <div>
                  <div className="metric-label">TOTAL BENES</div>
                  <div className="metric-value">{metrics.totalBenes.toLocaleString()}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>📅</span>
                <div>
                  <div className="metric-label">PERSON YEARS</div>
                  <div className="metric-value">{metrics.personYears.toLocaleString()}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>🎂</span>
                <div>
                  <div className="metric-label">AVG AGE</div>
                  <div className="metric-value">{Math.round(metrics.avgAge) || '0'}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>📊</span>
                <div>
                  <div className="metric-label">AVG RISK</div>
                  <div className="metric-value">{(metrics.avgRiskScore || 0).toFixed(3)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>⚠️</span>
                <div>
                  <div className="metric-label">PATIENTS WITH 4+ CC'S</div>
                  <div className="metric-value">
                    {metrics.patientsWithCC.toLocaleString()}
                    <span style={{fontSize: '16px', color: '#6b7280', marginLeft: '8px'}}>
                      ({((metrics.patientsWithCC / metrics.totalBenes) * 100).toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>

        <div className="charts-grid" style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              Membership by Chronic Condition
              {data.byChronicCondition.length > 15 && (
                <span style={{fontSize: '14px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px'}}>
                  (Top 15 of {data.byChronicCondition.length})
                </span>
              )}
            </h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart 
                data={data.byChronicCondition.slice(0, 15)} 
                layout="vertical"
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{fontSize: 12}} />
                <YAxis dataKey="name" type="category" width={180} style={{fontSize: '12px'}} interval={0} tick={{dy: 4}} />
                <Tooltip 
                  cursor={{ fill: '#e0f2fe' }}
                  content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                        <p style={{margin: 0}}><strong>{payload[0].payload.name}</strong></p>
                        <p style={{margin: '4px 0 0 0'}}>Count: {payload[0].value.toLocaleString()} ({payload[0].payload.percentage}%)</p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="membership" fill="#0284c7">
                  <LabelList dataKey="membership" position="insideRight" formatter={(value) => value.toLocaleString()} style={{fontSize: '12px', fill: 'white'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>Membership By Comorbidity Level</h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart 
                data={data.byComorbidityLevel.filter(item => item.level !== null)}
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="level" tick={{fontSize: 12}} />
                <YAxis tick={{fontSize: 12, dy: 4}} />
                <Tooltip 
                  cursor={{ fill: '#e0f2fe' }}
                  content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                        <p style={{margin: 0}}><strong>Level: {item.level}</strong></p>
                        <p style={{margin: '5px 0 0 0'}}>Count: {item.membership?.toLocaleString()} ({item.percentage}%)</p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="membership" fill="#0284c7">
                  <LabelList dataKey="membership" position="middle" formatter={(value) => value.toLocaleString()} style={{fontSize: '12px', fill: 'white'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="table-section" style={{marginTop: '30px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
            <h2 style={{margin: 0}}>Beneficiaries ({beneficiaries.length.toLocaleString()} records)</h2>
            <button 
              onClick={downloadCSV}
              style={{
                padding: '10px 20px',
                backgroundColor: '#0369a1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0284c7'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#0369a1'}
            >
              Download CSV
            </button>
          </div>
          <div style={{overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                  {beneficiaries.length > 0 && Object.keys(beneficiaries[0]).map((column) => (
                    <th key={column} style={{padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap'}}>
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {beneficiaries.map((row, index) => (
                  <tr key={`${row.BENE_MBI_ID}-${index}`} style={{borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'}}>
                    {Object.keys(row).map((column) => (
                      <td key={column} style={{padding: '12px', fontSize: '13px', color: '#1f2937', whiteSpace: 'nowrap'}}>
                        {row[column] !== null && row[column] !== undefined ? row[column] : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChronicConditionsDashboard;
